// Skeletons dynamiques pour AbracadaBati
// Animations fluides avec vagues de chargement

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

// Skeleton de base avec animation wave
export function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md bg-gray-200",
        "before:absolute before:inset-0",
        "before:translate-x-[-100%] before:animate-shimmer",
        "before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent",
        className
      )}
      {...props}
    />
  );
}

// Skeleton pour carte produit/artisan
export function CardSkeleton({ className }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn("rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100", className)}
    >
      {/* Image */}
      <Skeleton className="h-48 w-full rounded-none" />
      
      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Avatar + Name */}
        <div className="flex items-center gap-3">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
        
        {/* Description */}
        <div className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
        </div>
        
        {/* Tags */}
        <div className="flex gap-2">
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-14 rounded-full" />
        </div>
        
        {/* Button */}
        <Skeleton className="h-10 w-full rounded-xl mt-2" />
      </div>
    </motion.div>
  );
}

// Skeleton pour post réseau social
export function PostSkeleton({ className }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("bg-white rounded-2xl p-4 shadow-sm border border-gray-100", className)}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Skeleton className="w-12 h-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="w-8 h-8 rounded-full" />
      </div>
      
      {/* Content text */}
      <div className="space-y-2 mb-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      
      {/* Image */}
      <Skeleton className="h-56 w-full rounded-xl mb-4" />
      
      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex gap-6">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-16" />
        </div>
        <Skeleton className="w-8 h-8 rounded-full" />
      </div>
    </motion.div>
  );
}

// Skeleton pour story circulaire
export function StorySkeleton() {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative">
        <Skeleton className="w-16 h-16 rounded-full" />
        <div className="absolute inset-0 rounded-full border-2 border-gray-200 animate-pulse" />
      </div>
      <Skeleton className="h-3 w-12" />
    </div>
  );
}

// Skeleton pour offre d'emploi
export function JobSkeleton({ className }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn("bg-white rounded-xl p-4 shadow-sm border border-gray-100", className)}
    >
      <div className="flex gap-4">
        {/* Logo */}
        <Skeleton className="w-14 h-14 rounded-xl flex-shrink-0" />
        
        {/* Content */}
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <div className="flex gap-2 mt-2">
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
        </div>
        
        {/* Bookmark */}
        <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
      </div>
    </motion.div>
  );
}

// Skeleton pour produit marketplace
export function ProductSkeleton({ className }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn("bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100", className)}
    >
      {/* Image */}
      <Skeleton className="h-40 w-full rounded-none" />
      
      {/* Content */}
      <div className="p-3 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <div className="flex items-center justify-between pt-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      </div>
    </motion.div>
  );
}

// Skeleton pour message/conversation
export function MessageSkeleton({ isOwn = false }) {
  return (
    <div className={cn("flex gap-2 mb-3", isOwn ? "flex-row-reverse" : "")}>
      {!isOwn && <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />}
      <div className={cn("space-y-1", isOwn ? "items-end" : "")}>
        <Skeleton className={cn("h-10 rounded-2xl", isOwn ? "w-48" : "w-56")} />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  );
}

// Skeleton pour liste de conversations
export function ConversationSkeleton() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Skeleton className="w-12 h-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
      <div className="text-right space-y-2">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-5 w-5 rounded-full ml-auto" />
      </div>
    </div>
  );
}

// Skeleton pour statistique/KPI
export function StatSkeleton() {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <Skeleton className="h-3 w-24 mb-3" />
      <Skeleton className="h-8 w-20 mb-2" />
      <Skeleton className="h-3 w-16" />
    </div>
  );
}

// Grid de skeletons avec stagger animation
export function SkeletonGrid({ count = 6, type = "card", columns = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" }) {
  const SkeletonComponent = {
    card: CardSkeleton,
    post: PostSkeleton,
    job: JobSkeleton,
    product: ProductSkeleton,
    story: StorySkeleton,
    stat: StatSkeleton,
  }[type] || CardSkeleton;
  
  return (
    <div className={cn("grid gap-4", columns)}>
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
        >
          <SkeletonComponent />
        </motion.div>
      ))}
    </div>
  );
}

// Stories bar skeleton
export function StoriesBarSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto py-3 px-4 no-scrollbar">
      {Array.from({ length: 6 }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.08 }}
        >
          <StorySkeleton />
        </motion.div>
      ))}
    </div>
  );
}

// Feed skeleton complet (stories + posts)
export function FeedSkeleton() {
  return (
    <div className="space-y-4">
      {/* Stories */}
      <StoriesBarSkeleton />
      
      {/* Posts */}
      <div className="space-y-4 px-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + i * 0.15 }}
          >
            <PostSkeleton />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// Page skeleton wrapper avec animation d'entrée
export function PageSkeleton({ children, className }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={cn("min-h-screen", className)}
    >
      {children}
    </motion.div>
  );
}

export default Skeleton;
